<script setup lang="ts">
import { getCurrentUser, signIn, signInWithRedirect } from "aws-amplify/auth";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import BaseButton from "@/common/components/BaseButton.vue";
import useFormKitErrors from "@/common/composables/useFormKitErrors";
import {
  handleSwaggerOAuthBeforeEnter,
  useSwaggerOAuth,
} from "@/common/composables/useSwaggerOAuth";
import { BLACK_MWS_LOGO_URL } from "@/common/utils/constants";

import PlainLayout from "../layouts/PlainLayout.vue";
import GoogleLogo from "../misc/assets/google_logo.png";

definePage({
  meta: { layout: PlainLayout },
  beforeEnter: async (to) => {
    const swaggerResult = await handleSwaggerOAuthBeforeEnter(to);
    if (swaggerResult !== undefined) {
      return swaggerResult;
    }

    try {
      const user = await getCurrentUser();

      if (user) {
        return "/";
      }
    } catch {}
  },
});

const { onError } = useFormKitErrors("login-form");
const route = useRoute();
const { isSwaggerOAuthRequest, completeSwaggerOAuth } = useSwaggerOAuth();

const onSubmit = async () => {
  try {
    const redirectTo = route.redirectedFrom?.fullPath || "/";
    const redirectState = encodeURIComponent(JSON.stringify({ redirectTo }));

    await signInWithRedirect({
      provider: {
        idpIdentifier: "google_workspace",
      },
      customState: redirectState,
    });
  } catch (e) {
    onError(e);
  }
};

const router = useRouter();

const isLocalEnvironment = import.meta.env.VITE_MODE === "local-backend";
const isLocalSwaggerAuth = computed(
  () => isLocalEnvironment && isSwaggerOAuthRequest.value,
);
const loginForEndToEndTests = async (data: {
  email: string;
  password: string;
}) => {
  if (!isLocalEnvironment) {
    throw new Error("This login method is only intended for E2E tests.");
  }

  if (isSwaggerOAuthRequest.value) {
    await completeSwaggerOAuth(data.email, data.password, onError);

    return;
  }

  try {
    const redirectTo = route.redirectedFrom?.fullPath || "/";

    await signIn({
      username: data.email,
      password: data.password,
      options: {
        authFlowType: "USER_PASSWORD_AUTH",
      },
    });

    router.push(redirectTo);
  } catch (e) {
    onError(e);
  }
};
</script>

<template>
  <div class="flex flex-col items-center bg-gray-100 mt-24">
    <div class="bg-white p-12 rounded-md flex flex-col">
      <img
        class="h-12 w-auto mx-auto"
        :src="BLACK_MWS_LOGO_URL"
        alt="MWS"
      />
      <template v-if="isLocalSwaggerAuth">
        <p class="text-gray-500 text-sm mt-6 mb-2 text-center">
          Sign in to authorize Swagger UI
        </p>
      </template>
      <FormKitForm
        v-if="isLocalEnvironment"
        form-class="mt-6"
        :submit-attrs="{
          'data-cy': 'login-submit-button',
        }"
        @submit="loginForEndToEndTests"
      >
        <FormKit
          data-cy="login-email-input"
          type="email"
          name="email"
          label="Email"
        />
        <FormKit
          data-cy="login-password-input"
          type="password"
          name="password"
          label="Password"
        />
      </FormKitForm>
      <template v-else>
        <FormKitForm
          id="login-form"
          :actions="false"
          messages-class="order-2"
          form-class="mt-4 -mb-2"
          @submit="onSubmit"
        >
          <FormKit
            type="submit"
            input-class="min-w-72 w-max"
          >
            <span class="flex justify-center grow">
              <img
                class="size-5 mr-2"
                :src="GoogleLogo"
              />
              Log in via Google Workspace
            </span>
          </FormKit>
        </FormKitForm>
        <BaseButton
          variant="secondary"
          data-cy="login-submit-button"
          label="Log in using username & password"
          @click="signInWithRedirect"
        />
      </template>
    </div>
  </div>
</template>
